import React from 'react';
import {
  Box,
  Typography,
  Button,
  Avatar,
  CircularProgress,
} from '@mui/material';
import { BackButton } from '../common/BackButton';
import { getDoctorPhotoUrl } from '../../services/clinicService';
import { OpenInNewOutlined } from '@mui/icons-material';

interface Doctor {
  id: string;
  name: string;
  description?: string;
  photo?: string;
  image?: string;
  specializations?: any[];
  branches?: any[];
  appointmentLink?: string;
}

export const DoctorDetailCard: React.FC<{
  doctor: Doctor;
  fullDoctor: Doctor | null;
  isLoadingDetails: boolean;
  onBack: () => void;
  onBook: (id: string) => void;
  isSubmitting: boolean;
  clinicName: string;
  workSchedule: any;
  showBranches?: boolean;
}> = ({
  doctor,
  fullDoctor,
  isLoadingDetails,
  onBack,
  onBook,
  isSubmitting,
}) => {
  const d = fullDoctor || doctor;
  const specNames =
    d.specializations?.map((s: any) => s.name).join(', ') || '';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        pb: '100px',
      }}
    >
      <BackButton onClick={onBack} />

      {/* Фото */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          mb: { xs: 2, sm: 3 },
          pt: { xs: 1.5, sm: 2 },
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: { xs: '100%', sm: '480px', md: '600px' },
            paddingBottom: '67%',
            position: 'relative',
            borderRadius: '16px',
            overflow: 'hidden',
            backgroundColor: '#E8E8E8',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
            }}
          >
            {getDoctorPhotoUrl(d.image) ? (
              <img
                src={getDoctorPhotoUrl(d.image)!}
                alt={d.name}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                }}
              />
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'absolute',
                  inset: 0,
                }}
              >
                <Avatar
                  sx={{
                    width: { xs: 60, sm: 80 },
                    height: { xs: 60, sm: 80 },
                    fontSize: { xs: 24, sm: 36 },
                  }}
                >
                  {d.name?.charAt(0) || '?'}
                </Avatar>
              </Box>
            )}
          </Box>
        </Box>

        {specNames && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 12,
              left: 12,
              backgroundColor: 'rgba(0,122,255,0.85)',
              borderRadius: '12px',
              padding: '6px 12px',
            }}
          >
            <Typography
              sx={{ fontSize: 12, fontWeight: 600, color: '#fff' }}
            >
              {specNames}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Контент */}
      <Box sx={{ px: { xs: 0, sm: 1 } }}>
        {isLoadingDetails ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CircularProgress size={24} sx={{ color: '#007AFF' }} />
          </Box>
        ) : d.description ? (
          <Box
            sx={{
              backgroundColor: '#fff',
              borderRadius: '16px',
              p: { xs: '12px', sm: '16px', md: '20px' },
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              mb: 2,
              opacity: 0.85,
              maxWidth: { xs: '100%', sm: '359px', md: '480px' },
              margin: '0 auto 16px auto',
              width: '100%',
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: 18, sm: 20, md: 22 },
                fontWeight: 700,
                color: '#212121',
                mb: 1,
              }}
            >
              {d.name}
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: 14, sm: 16, md: 17 },
                color: '#212121',
                opacity: 0.85,
              }}
            >
              {d.description}
            </Typography>

            {d.appointmentLink && (
              <Box
                onClick={() => window.open(d.appointmentLink, '_blank')}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mt: 1,
                  cursor: 'pointer',
                  transition: 'opacity 0.2s ease',
                  '&:hover': {
                    opacity: 0.7,
                  },
                }}
              >
                <Typography
                  sx={{
                    color: '#007AFF',
                    fontWeight: 600,
                    fontSize: { xs: 12, sm: 14 },
                  }}
                >
                  Подробнее
                </Typography>
                <OpenInNewOutlined
                  sx={{ color: '#007AFF', fontSize: 18 }}
                />
              </Box>
            )}
          </Box>
        ) : (
          <Typography
            sx={{
              fontSize: { xs: 13, sm: 14 },
              color: 'rgba(33,33,33,0.5)',
              mb: 2,
              maxWidth: '359px',
              margin: '0 auto',
            }}
          >
            Описание отсутствует
          </Typography>
        )}
      </Box>

      {/* Кнопка */}
      <Button
        fullWidth
        variant="contained"
        onClick={() => onBook(d.id)}
        disabled={isSubmitting}
        sx={{
          position: 'fixed',
          bottom: 90,
          left: 0,
          right: 0,
          maxWidth: '100%',
          mx: 'auto',
          px: 2,
          height: { xs: 48, sm: 55, md: 60 },
          width: { xs: 'calc(100% - 32px)', sm: '480px' },
          borderRadius: '22px',
          backgroundColor: '#007AFF',
          textTransform: 'none',
          fontSize: { xs: 14, sm: 16, md: 18 },
          boxShadow: '0 4px 12px rgba(0,122,255,0.3)',
          zIndex: 10,
          '&:hover': {
            backgroundColor: '#005FCC',
            boxShadow: '0 6px 16px rgba(0,122,255,0.4)',
          },
        }}
      >
        {isSubmitting ? (
          <CircularProgress size={22} color="inherit" />
        ) : (
          'Записаться на приём'
        )}
      </Button>
    </Box>
  );
};